import React, { useState } from "react";
import moment from "moment";
import { Box, Typography, Divider } from "@material-ui/core";
import { useSelector } from "react-redux";
import {
  CustomBadge,
  CustomButton,
  CustomCard,
  CustomTimeline,
} from "../../../../../../Components";
import { CustomMenu } from "../../../../../../Components";
import {
  useIsAr,
  useSelectedTheme,
  useTranslate,
} from "../../../../../../Hooks";
import { ReplyActivityModalDialog } from "../../../../NewContactsView/ContactOverview/UI/ContactActivityItemTimeLine/ReplyActivityModalDialog";

// Icons
import {
  ArrowNarrowRight,
  ArrowNarrowLeft,
  MoreVertIcon,
  ActivityIcon,
  ReverseRightIcon,
} from "../../../../../../assets/icons";

// styles
import useStyles from "./styles";

function TimelinePrefixContent({ item }) {
  const styles = useStyles();

  const date = moment(item.createdOn);
  const formattedDate = date.format("MMM D");
  const formattedYear = date.format("YYYY");
  const formattedTime = date.format("h:mm a");

  return (
    <Box className={styles.prefixContentWrapper}>
      <div className={styles.prefixItemMonth}>{formattedDate}</div>
      <div className={styles.prefixItemYear}>{formattedYear}</div>
      <div className={styles.prefixItemTime}>{formattedTime}</div>
    </Box>
  );
}

function TimelineItemContent({ item }) {
  const styles = useStyles();
  const theme = useSelector((store) => store.theme);
  const rate = item?.activityRateName?.replace(/\s+/g, "").toLowerCase();

  const { translate } = useTranslate("AfterSalesClientProfileView");
  const { isAr } = useIsAr(theme.themeDirection);
  const {
    theme: { palette },
  } = useSelectedTheme();

  const [activeItem, setActiveItem] = useState(null);
  const [openReplyActivityDialog, setOpenReplyActivityDialog] = useState(false);
  const [isExpandedLeadSourceText, setIsExpandedLeadSourceText] =
    useState(false);

  const MAX_WORDS_SIZE = 14;
  const getContent = (content = "", showFullText) => {
    const firstNewLineIndex = content.indexOf("\n");

    if (firstNewLineIndex !== -1 && !showFullText) {
      return `${content.slice(0, firstNewLineIndex)}...`;
    }
    const allContent = content && content.split(" ");
    return allContent && allContent.length > MAX_WORDS_SIZE && !showFullText
      ? `${allContent.slice(0, MAX_WORDS_SIZE).join(" ")}...`
      : content;
  };

  return (
    <Box className={styles.timelineContentWrapper}>
      <CustomCard
        boxShadow="xs"
        borderRadius="xl"
        borderColor="secondary"
        classes={styles.activityCard}
      >
        <Box className={styles.activityInfoContainer}>
          <Box className={styles.leftSection}>
            <Box className={styles.iconWrapper}>
              <ActivityIcon
                height="24"
                width="24"
                fill={palette.foreground.navigation["nav-item-button-icon-fg"]}
              />
            </Box>
            <Box className={styles.nameStatusContainer}>
              <Typography className={styles.activityName}>
                {item?.activityTypeName || "N/A"}
              </Typography>
              <CustomBadge
                label={item?.activityRateName}
                SizeVariant={"small"}
                BackgroundColor={
                  rate === "neutral"
                    ? palette.utility.warning_50
                    : rate === "positive" || rate === "superpositive"
                    ? palette.utility.success_50
                    : rate === "negative" || rate === "supernegative"
                    ? palette.utility.error_50
                    : palette.utility.success_50
                }
                BorderColor={
                  rate === "neutral"
                    ? palette.utility.warning_200
                    : rate === "positive" || rate === "superpositive"
                    ? palette.utility.success_200
                    : rate === "negative" || rate === "supernegative"
                    ? palette.utility.error_200
                    : palette.utility.success_200
                }
                Color={
                  rate === "neutral"
                    ? palette.utility.warning_700
                    : rate === "positive" || rate === "superpositive"
                    ? palette.utility.success_700
                    : rate === "negative" || rate === "supernegative"
                    ? palette.utility.error_700
                    : palette.utility.success_700
                }
              />
            </Box>
          </Box>
          <Box className={styles.rightSection}>
            <Box className={styles.createdAssignedContainer}>
              <Typography className={styles.infoItem} component="span">
                {translate("created-by")}
                <strong className={styles.createdbyAssignedtoValue}>
                  {item?.createdByName ? item?.createdByName : "N/A"}
                </strong>
              </Typography>
              <Box className={styles.arrowIcon}>
                {isAr ? (
                  <ArrowNarrowLeft
                    width="24"
                    height="24"
                    fill={
                      palette.foreground.navigation["nav-item-button-icon-fg"]
                    }
                  />
                ) : (
                  <ArrowNarrowRight
                    width="24"
                    height="24"
                    fill={
                      palette.foreground.navigation["nav-item-button-icon-fg"]
                    }
                  />
                )}
                <Typography className={styles.infoItem} component="span">
                  {translate("assign-to")}
                  <strong className={styles.createdbyAssignedtoValue}>
                    {item?.assignedTo ? item?.assignedTo : "N/A"}
                  </strong>
                </Typography>
              </Box>
            </Box>
            {item?.comments && (
              <Box>
                <Divider className={styles.divider} />
                <Box className={styles.commentContainer}>
                  <Typography className={styles.commentValue}>
                    {getContent(item?.comments, isExpandedLeadSourceText)}
                  </Typography>
                  {(item?.comments?.split(" ").length > MAX_WORDS_SIZE ||
                    item?.comments?.includes("\n")) && (
                    <CustomButton
                      boxShadow="none"
                      size="sm"
                      variant="text"
                      color="tertiary"
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                      }}
                      onClick={() => {
                        setIsExpandedLeadSourceText(!isExpandedLeadSourceText);
                      }}
                    >
                      {isExpandedLeadSourceText
                        ? translate("hide")
                        : translate("show")}
                    </CustomButton>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
        <Box>
          {item?.isReplyAble && (
            <CustomMenu
              menuItems={[
                {
                  icon: (
                    <ReverseRightIcon
                      width="16"
                      height="16"
                      fill={palette.foreground.quarterary}
                    />
                  ),
                  handler: () => {
                    setOpenReplyActivityDialog(true);
                    setActiveItem(item);
                  },
                  text: translate("reply"),
                },
              ]}
              activeIconClass={styles.menuActiveIconClass}
              defaultIconClass={styles.menuDefaultIconClass}
              customTransformOrigin={
                isAr
                  ? { vertical: "top", horizontal: "left" }
                  : { vertical: "top", horizontal: "right" }
              }
            >
              <MoreVertIcon
                width="20"
                height="20"
                fill={palette.foreground.navigation["nav-item-button-icon-fg"]}
              />
            </CustomMenu>
          )}
        </Box>
      </CustomCard>
      {openReplyActivityDialog && (
        <ReplyActivityModalDialog
          open={openReplyActivityDialog}
          close={() => {
            setActiveItem(null);
            setOpenReplyActivityDialog(false);
          }}
          activeItem={activeItem}
          onSave={() => {
            setOpenReplyActivityDialog(false);
            setActiveItem(null);
          }}
          setOpenReplyActivityDialog={setOpenReplyActivityDialog}
        />
      )}
    </Box>
  );
}

function ActivityItemTimeline({ timelineItems }) {
  return (
    <CustomTimeline
      timelineData={timelineItems}
      component={TimelineItemContent}
      timelinePrefixComponent={TimelinePrefixContent}
      timelineDotTopMargin="22px"
    />
  );
}

export default ActivityItemTimeline;
